import {
  ACTIVITY_REQUEST_OPENING_RULE_LABELS,
  REGISTRATION_MODE_LABELS
} from '@/constants/activity-requests.constants'
import type { ModuleViewSlaState, QueueTone } from '@/components/designSystem/QueueViews/types'
import type { ActivityRequestOpeningRule, RegistrationMode } from '@/types/activity-requests.types'
import {
  DEFAULT_UTC_OFFSET,
  formatDateInOffset,
  formatHourMinuteInOffset,
  formatUtcOffsetLabel,
  normalizeUtcOffset
} from '@/utils/date'

/**
 * Formatação da aba Solicitações (M2-A/M2-C). Regra de negócio (buckets, prazo, estado) é do
 * backend; aqui só se escreve o texto. Tudo puro: o "agora" entra por parâmetro.
 */

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const pad = (n: number): string => String(n).padStart(2, '0')

/** `42 min` · `5 h` · `1 h 05` · `1 d 17 h` · `3 d` (valor absoluto). */
export function formatRelativeDuration(ms: number): string {
  const abs = Math.abs(ms)
  if (abs < HOUR) return `${Math.max(1, Math.floor(abs / MINUTE))} min`
  if (abs < DAY) {
    const hours = Math.floor(abs / HOUR)
    const minutes = Math.floor((abs % HOUR) / MINUTE)
    return minutes ? `${hours} h ${pad(minutes)}` : `${hours} h`
  }
  const days = Math.floor(abs / DAY)
  const hours = Math.floor((abs % DAY) / HOUR)
  return hours ? `${days} d ${hours} h` : `${days} d`
}

/** "Vence em X" / "Vencida há X" contra o relógio corrigido; `null` sem prazo ou relógio. */
export function formatDeadlineRelative(
  deadlineIso: string | null,
  nowMs: number | null
): string | null {
  if (!deadlineIso || nowMs === null) return null
  const deadline = Date.parse(deadlineIso)
  if (Number.isNaN(deadline)) return null
  const remaining = deadline - nowMs
  return remaining > 0
    ? `Vence em ${formatRelativeDuration(remaining)}`
    : `Vencida há ${formatRelativeDuration(remaining)}`
}

const ZONE_NAMES: Record<string, string> = {
  '-02:00': 'horário de Fernando de Noronha',
  '-03:00': 'horário de Brasília',
  '-04:00': 'horário do Amazonas',
  '-05:00': 'horário do Acre'
}

const offsetMinutes = (normalized: string): number => {
  const sign = normalized.startsWith('-') ? -1 : 1
  const [hours, minutes] = normalized.slice(1).split(':').map(Number)
  return sign * (hours * 60 + minutes)
}

/** `horário de Brasília (UTC-3)` — derivado do offset do plantão (M2-A balão 3). */
export function timezoneLabel(offset: string | null | undefined): string {
  const normalized = normalizeUtcOffset(offset || DEFAULT_UTC_OFFSET)
  const utc = formatUtcOffsetLabel(offsetMinutes(normalized))
  return `${ZONE_NAMES[normalized] ?? 'fuso do plantão'} (${utc})`
}

/** `dd/MM/yyyy HH:mm` no fuso do plantão; `null` com ISO ausente/inválido. */
export function formatDateTimeInStation(iso: string | null, offset: string): string | null {
  if (!iso) return null
  const date = formatDateInOffset(iso, offset)
  const time = formatHourMinuteInOffset(iso, offset)
  return date && time ? `${date} ${time}` : null
}

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

export function formatStationPeriod(
  startAt: string | null,
  endAt: string | null,
  offset: string
): { weekday: string; date: string; range: string; duration: string } {
  // Plantão sem horário previsto (o backend manda `null`): placeholders, nunca "NaN".
  if (!startAt || !endAt) return { weekday: '', date: '—', range: '--:-- – --:--', duration: '—' }
  const normalized = normalizeUtcOffset(offset || DEFAULT_UTC_OFFSET)
  const start = Date.parse(startAt)
  const end = Date.parse(endAt)
  const localStart = new Date(start + offsetMinutes(normalized) * MINUTE)
  const totalMinutes = Math.max(0, Math.round((end - start) / MINUTE))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return {
    weekday: Number.isNaN(start) ? '' : WEEKDAYS[localStart.getUTCDay()],
    date: formatDateInOffset(startAt, normalized) ?? '—',
    range: `${formatHourMinuteInOffset(startAt, normalized) ?? '--:--'} – ${
      formatHourMinuteInOffset(endAt, normalized) ?? '--:--'
    }`,
    duration: minutes ? `${hours} h ${pad(minutes)}` : `${hours} h`
  }
}

/** `+4 min` / `-12 min` / `no horário`; `null` sem efetivo ou previsto. */
export function formatLegDeviation(
  effectiveIso: string | null,
  expectedIso: string | null
): string | null {
  if (!effectiveIso || !expectedIso) return null
  const diff = Math.round((Date.parse(effectiveIso) - Date.parse(expectedIso)) / MINUTE)
  if (Number.isNaN(diff)) return null
  if (diff === 0) return 'no horário'
  return `${diff > 0 ? '+' : '-'}${Math.abs(diff)} min`
}

export function openingRuleLabel(rule: string | null): string {
  if (!rule) return 'Regra não registrada (solicitação antiga)'
  return ACTIVITY_REQUEST_OPENING_RULE_LABELS[rule as ActivityRequestOpeningRule] ?? rule
}

/**
 * `48 h` (padrão do app, como o mockup escreve) · `7 dias` · `30 dias`; `null` sem janela.
 * Dias só acima de 48 h e em múltiplo de 24: "2 dias" esconderia a régua de 48 h da Q18.
 */
export function sendWindowLabel(hours: number | null | undefined): string | null {
  if (hours === null || hours === undefined || hours <= 0) return null
  if (hours > 48 && hours % 24 === 0) return `${hours / 24} dias`
  return `${hours} h`
}

export function registrationModeLabel(mode: string | null | undefined): string {
  if (!mode) return 'Modalidade não informada'
  return REGISTRATION_MODE_LABELS[mode as RegistrationMode] ?? mode
}

/** Cor do prazo: SÓ o estado do servidor (§6.3) — nunca `new Date() > deadline`. */
export function slaTone(state: ModuleViewSlaState | null | undefined): QueueTone {
  switch (state) {
    case 'OVERDUE':
      return 'crit'
    case 'ATTENTION':
    case 'URGENT':
      return 'warn'
    case 'ON_TIME':
      return 'ok'
    default:
      return 'neutral'
  }
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

export const formatBRL = (amount: number): string => BRL.format(amount)
